import { Box, IconButton, Button, Input, Typography, useTheme, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { useEffect, useState } from "react";
import { DataGrid, GridToolbarQuickFilter } from "@mui/x-data-grid";
import { useAuth } from "../../../AuthContext";
import { tokens } from "../../../theme";
import { ToastContainer, toast } from "react-toastify";
import { Form } from "react-bootstrap";
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import AddIcon from '@mui/icons-material/Add';
import 'react-toastify/dist/ReactToastify.css';

const Settings = () => {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState(null);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { getData, addData, updateData, deleteData } = useAuth();

  // Form state
  const [formData, setFormData] = useState({
    site_name: '',
    description: '',
    short_description: '',
    aboutSteam: '',
    stemEducation: '',
    logo: '',
    mission: '',
    vision: '',
    value: ''
  });

  useEffect(() => {
    async function fetchData() {
      try {
        const response = await getData('proccess.php','websettings'); // Fetch settings data
        if (response.success) {
          setTeams(response.data);
        } else {
          toast.error(response.message || "Failed to load settings data");
        }
      } catch (err) {
        toast.error("Network error: Failed to fetch settings");
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [getData]);

  const handleAddClick = () => {
    setFormData({
      site_name: '',
      description: '',
      short_description: '',
      aboutSteam: '',
      stemEducation: '',
      logo: '',
      mission: '',
      vision: '',
      value: ''
    });
    setOpenAddDialog(true);
  };

  const handleEditClick = (team) => {
    setSelectedTeam(team);
    setFormData({
      site_name: team.site_name || '',
      description: team.description || '',
      short_description: team.short_description || '',
      aboutSteam: team.aboutSteam || '',
      stemEducation: team.stemEducation || '',
      logo: team.logo || '',
      mission: team.mission || '',
      vision: team.vision || '',
      value: team.value || ''
    });
    setOpenEditDialog(true);
  };

  const handleDeleteClick = (team) => {
    setTeamToDelete(team);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await deleteData('proccess.php','websettings', teamToDelete.id); // Delete settings entry by ID
      if (response.success) {
        toast.success(`Settings entry deleted successfully`);
        setTeams(teams.filter(team => team.id !== teamToDelete.id));
      } else {
        toast.error(response.message || "Failed to delete settings entry");
      }
    } catch (err) {
      toast.error("Network error: Failed to delete settings entry");
      console.error("Delete error:", err);
    }
    setOpenDeleteDialog(false);
    setTeamToDelete(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    setFormData(prev => ({
      ...prev,
      logo: e.target.files[0]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let response;
      if (selectedTeam) {
        // Update existing settings entry
        response = await updateData('proccess.php','websettings', formData, selectedTeam.id); // Update settings
        if (response.success) {
          toast.success(`Settings entry updated successfully`);
          setTeams(response.data);
        }
      } else {
        // Add new settings entry
        response = await addData('proccess.php','settings', formData); // Add settings
        if (response.success) {
          toast.success(`Settings entry added successfully`);
          setTeams(response.data);
        }
      }
      
      if (!response.success) {
        toast.error(response.message || "Operation failed");
      }
    } catch (err) {
      toast.error("Network error: Operation failed");
      console.error("Operation error:", err);
    }
    setOpenEditDialog(false);
    setOpenAddDialog(false);
    setSelectedTeam(null);
  };

  const columns = [
    { field: "id", headerName: "ID", flex: 0.5, minWidth: 20 },
    {
      field: "site_name",
      headerName: "Company Name",
      cellClassName: "name-column--cell",
      flex: 1,
      minWidth: 120
    },
    { field: "description", headerName: "About Us", flex: 1, minWidth: 120 },
    { field: "short_description", headerName: "Hero Description", flex: 1, minWidth: 120 },
    { field: "aboutSteam", headerName: "About Mindsinaction", flex: 1, minWidth: 120 },
    { field: "stemEducation", headerName: "Steam Education", flex: 1, minWidth: 120 },
    { 
      field: "logo", 
      headerName: "Logo", 
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Box width={50} height={50}>
          {params.value ? (
            <img
              src={`https://mindsinaction.com.na/assets/media/pictures/${params.value}`}
              alt={params.value || "Module"}
              style={{ width: 50, height: 50, objectFit: "cover", cursor: 'pointer' }}
              onClick={() => window.open(`https://mindsinaction.com.na/assets/media/pictures/${params.value}`, "_blank")}
            />
          ) : (
            "No image"
          )}
        </Box>
      )
    },
    { field: "mission", headerName: "Mission", flex: 1, minWidth: 120 },
    { field: "vision", headerName: "Vision", flex: 1, minWidth: 120 },
    { field: "value", headerName: "Value", flex: 1, minWidth: 120 },
    { 
      field: "actions", 
      headerName: "Action", 
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Box display="flex" gap="8px">
          <IconButton 
            sx={{ color: colors.blue[400] }}
            onClick={() => handleEditClick(row)}
          >
            <EditNoteOutlinedIcon />
          </IconButton>
          <IconButton 
            sx={{ color: colors.orange[400] }}
            onClick={() => handleDeleteClick(row)}
          >
            <DeleteForeverOutlinedIcon />
          </IconButton>
        </Box>
      )
    }
  ];

  const NoDataOverlay = () => {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="100%"
        color={colors.dark[400]}
      >
        <Typography variant="h6">No data available</Typography>
      </Box>
    );
  };

  return (
    <Box m={"10px"}>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      
      {/* Add/Edit Dialog */}
      <Dialog 
        open={openEditDialog || openAddDialog} 
        onClose={() => {
          setOpenEditDialog(false);
          setOpenAddDialog(false);
          setSelectedTeam(null);
        }} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>{selectedTeam ? "Edit Settings Entry" : "Add New Settings Entry"}</DialogTitle>
        <DialogContent>
          <Form onSubmit={handleSubmit} style={{width: "100%", padding: "1rem"}}>
            <Box display={"flex"} gap={"1rem"} width={"100%"} mb={"1rem"} flexWrap="wrap">
              <TextField 
                style={{width: "100%"}} 
                id="site_name" 
                name="site_name"
                label="Company Name" 
                variant="standard" 
                value={formData.site_name}
                onChange={handleFormChange}
                required
              />
              <TextField
                style={{width: "100%"}}
                id="mission"
                name="mission"
                label="Mission"
                multiline
                maxRows={4}
                variant="standard"
                value={formData.mission}
                onChange={handleFormChange}
                required
              />
              <TextField
                style={{width: "100%"}}
                id="vision"
                name="vision"
                label="Vision"
                multiline
                maxRows={4}
                variant="standard"
                value={formData.vision}
                onChange={handleFormChange}
                required
              />
              <TextField
                style={{width: "100%"}}
                id="value"
                name="value"
                label="Value"
                multiline
                maxRows={4}
                variant="standard"
                value={formData.value}
                onChange={handleFormChange}
                required
              />
              <Box width={"100%"}>
                <label>Company Logo</label>
                <Input 
                  type="file" 
                  style={{width: "100%"}} 
                  id="logo" 
                  name="logo"
                  onChange={handleFileChange}
                  required={!selectedTeam}
                />
              </Box>
            </Box>
            <Box width={"100%"} display={"flex"} flexWrap={'wrap'} gap={"1rem"}>
              <TextField
                style={{flex: 1, minWidth: "300px"}}
                id="description"
                name="description"
                label="About Us"
                multiline
                minRows={4}
                maxRows={8}
                variant="standard"
                value={formData.description}
                onChange={handleFormChange}
                required
              />
              <TextField
                style={{flex: 1, minWidth: "300px"}}
                id="short_description"
                name="short_description"
                label="Hero Description"
                multiline
                minRows={4}
                maxRows={8}
                variant="standard"
                value={formData.short_description}
                onChange={handleFormChange}
                required
              />
              <TextField
                style={{flex: 1, minWidth: "300px"}}
                id="aboutSteam"
                name="aboutSteam"
                label="About Mindsinaction"
                multiline
                minRows={4}
                maxRows={8}
                variant="standard"
                value={formData.aboutSteam}
                onChange={handleFormChange}
                required
              />
              <TextField
                style={{flex: 1, minWidth: "300px"}}
                id="stemEducation"
                name="stemEducation"
                label="Steam Education"
                multiline
                minRows={4}
                maxRows={8}
                variant="standard"
                value={formData.stemEducation}
                onChange={handleFormChange}
                required
              />
            </Box>
          </Form>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setOpenEditDialog(false);
              setOpenAddDialog(false);
            }}
            sx={{ color: colors.dark[400] }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            sx={{
              backgroundColor: colors.blue[400],
              '&:hover': {
                backgroundColor: colors.blue[500],
              }
            }} 
            variant="contained" 
            endIcon={<SendIcon />}
          >
            {selectedTeam ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{teamToDelete?.site_name}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleDeleteConfirm}
            sx={{
              backgroundColor: colors.red[400],
              '&:hover': {
                backgroundColor: colors.red[500],
              }
            }} 
            variant="contained" 
            startIcon={<DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Box mb={"30px"} ml={"15px"} mr={"15px"} display={"flex"} alignItems={"center"} justifyContent= {"space-between"}>
          <Box flex={1}>
              <Typography
                  variant="h4"
                  color={colors.dark[400]}
                  fontWeight={"bold"}
                  sx={{mb: "5px"}}
              >Web Settings</Typography>
              <Typography variant="h5" color={colors.blue[400]}>Managing the company's settings</Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddClick}
            sx={{
              backgroundColor: colors.blue[400],
              '&:hover': {
                backgroundColor: colors.blue[500],
              }
            }}
          >
            Add Entry
          </Button>
      </Box>
      <Divider component="" />
      <Box sx={{ height: "45vh", width: "100%" }} backgroundColor = {colors.dark[300]} padding={".2rem"} mt={"1rem"}>
          <DataGrid
            rows={teams}
            columns={columns}
            getRowId={(row) => row.id}
            loading={loading}
            sx={{
                border: "none",
                ".MuiDataGrid-cell": {
                  borderBottom: "none",
                  backgroundColor: "none"
                },
                ".MuiDataGrid-toolbarContainer": {
                  padding: "10px",
                  backgroundColor: colors.primary[900],
                  borderBottom: "none",
                },
                ".MuiDataGrid-quickFilter": {
                  padding: "8px",
                  borderRadius: "4px",
                  border: `1px solid ${colors.primary[100]}`,
                },
                ".MuiDataGrid-columnHeaders": {
                  backgroundColor: colors.primary[900],
                },
                ".MuiDataGrid-footerContainer": {
                  backgroundColor: colors.primary[900],
                },
            }}
            slots={{
                toolbar: () => (
                <div>
                    <GridToolbarQuickFilter />
                </div>
                ),
                noRowsOverlay: NoDataOverlay,
            }}
            slotProps={{
                toolbar: {
                showQuickFilter: true,
                quickFilterProps: {
                    debounceMs: 500,
                    placeholder: "Search...",
                },
                },
            }}
          />
      </Box>
    </Box>
  );
};

export default Settings;